// runSequentialStatementEdits — apply a sequence of edits against the SAME
// statement, re-resolving the statement id before each step.
//
// Why: each `commitEdit` re-parses the buffer and the resolver rotates
// statement ids. A naive loop that reuses one captured `editContext.statementId`
// only lands its first edit — the stale id no longer resolves on iteration 2+,
// `applyStructuralEdit` throws, and the un-awaited loop aborts. We capture the
// statement's `startOffset` once (stable while we only append within the
// statement) and re-find the current id by offset each iteration.
//
// This targets the id-based apply path (no `statementInfo`); the 3 column
// pickers + the aggregate "Fix all" prompt all operate on top-level statements,
// which is exactly that path.

import type { CommitEditCtx } from './commitEdit';
import type { VqeEditContext } from './editContext';
import { toCommitCtx } from './editContext';

/**
 * Precondition for `apply`: each edit must NOT move the target statement's
 * start offset (append/insert WITHIN the statement only — never prepend a new
 * leading statement or shift its start). Re-resolution keys on the original
 * `startOffset`; an edit that shifts it would break id re-resolution on the
 * next iteration. Every current caller (the 3 column pickers + the aggregate
 * "Fix all" prompt) appends within the statement, so this holds.
 */
export async function runSequentialStatementEdits<T>(
  editContext: VqeEditContext,
  items: readonly T[],
  apply: (item: T, ctx: CommitEditCtx) => Promise<void>,
): Promise<void> {
  const { service, documentId } = editContext;
  const startOffset = service
    .getParsedBuffer(documentId)
    ?.documentStructure.statements.find((s) => s.id === editContext.statementId)?.range.startOffset;

  for (const item of items) {
    // Re-resolve the current statement id (ids rotate on each re-parse). Fall
    // back to the original id for the first iteration / single-statement docs.
    const statements = service.getParsedBuffer(documentId)?.documentStructure.statements ?? [];
    const current =
      startOffset != null
        ? statements.find((s) => s.range.startOffset === startOffset)
        : undefined;
    const statementId = current?.id ?? editContext.statementId;
    await apply(item, toCommitCtx({ ...editContext, statementId }));
  }
}
